/**
 * Offline Mutation Queue
 * 
 * Stores mutations in IndexedDB when offline, allowing them to be synced
 * automatically when the user comes back online.
 */

import { get, set, del, createStore, keys } from 'idb-keyval';

// Create a dedicated IndexedDB store for mutations
const mutationStore = createStore('reactor-mutations', 'mutations');

export interface QueuedMutation {
  id: string;
  type: string; // e.g., 'POST', 'PUT', 'PATCH'
  url: string;
  payload: any; // Request body/payload (serialized)
  isFormData?: boolean; // Flag to indicate if payload was FormData
  headers?: Record<string, string>;
  createdAt: number;
  synced: boolean;
  retryCount?: number;
}

/**
 * Convert FormData to a serializable object
 */
async function serializeFormData(formData: FormData): Promise<{ data: Record<string, any>, files: Array<{ name: string, data: string, type: string }> }> {
  const data: Record<string, any> = {};
  const files: Array<{ name: string, data: string, type: string }> = [];

  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string') {
      data[key] = value;
      continue;
    }

    // Convert File/Blob to base64 for storage
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String.split(',')[1] || base64String); // Remove data URL prefix if present
      };
      reader.onerror = reject;
      reader.readAsDataURL(value);
    });

    files.push({
      name: key,
      data: base64,
      type: value.type || 'application/octet-stream',
    });
  }

  return { data, files };
}

/**
 * Reconstruct FormData from serialized format
 */
function deserializeFormData(serialized: { data: Record<string, any>, files: Array<{ name: string, data: string, type: string }> }): FormData {
  const formData = new FormData();

  // Add regular fields
  for (const [key, value] of Object.entries(serialized.data)) {
    formData.append(key, value);
  }

  // Reconstruct files
  for (const file of serialized.files) {
    // Convert base64 back to Blob
    const byteCharacters = atob(file.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: file.type });
    formData.append(file.name, blob, file.name);
  }

  return formData;
}

/**
 * Generate a unique ID for a mutation
 */
function generateMutationId(): string {
  return `mutation_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Add a mutation to the offline queue
 */
export async function addMutation(
  type: string,
  url: string,
  payload: any,
  headers?: Record<string, string>
): Promise<string> {
  let serializedPayload = payload;
  let isFormData = false;

  // Handle FormData by serializing it
  if (payload instanceof FormData) {
    isFormData = true;
    serializedPayload = await serializeFormData(payload);
  }

  const mutation: QueuedMutation = {
    id: generateMutationId(),
    type,
    url,
    payload: serializedPayload,
    isFormData,
    headers,
    createdAt: Date.now(),
    synced: false,
    retryCount: 0,
  };

  await set(mutation.id, mutation, mutationStore);
  console.log('[OfflineQueue] Added mutation to queue:', mutation.id, type, url, isFormData ? '(FormData)' : '');
  return mutation.id;
}

/**
 * Get all pending (unsynced) mutations
 */
export async function getPendingMutations(): Promise<QueuedMutation[]> {
  try {
    const allKeys = await keys(mutationStore);
    const mutations: QueuedMutation[] = [];

    for (const key of allKeys) {
      if (typeof key === 'string') {
        const mutation = await get<QueuedMutation>(key, mutationStore);
        if (mutation && !mutation.synced) {
          mutations.push(mutation);
        }
      }
    }

    // Sort by creation time (oldest first)
    mutations.sort((a, b) => a.createdAt - b.createdAt);
    return mutations;
  } catch (error) {
    console.error('[OfflineQueue] Error getting pending mutations:', error);
    return [];
  }
}

/**
 * Mark a mutation as synced
 */
export async function markSynced(mutationId: string): Promise<void> {
  try {
    const mutation = await get<QueuedMutation>(mutationId, mutationStore);
    if (mutation) {
      mutation.synced = true;
      await set(mutationId, mutation, mutationStore);
      console.log('[OfflineQueue] Marked mutation as synced:', mutationId);
    }
  } catch (error) {
    console.error('[OfflineQueue] Error marking mutation as synced:', error);
  }
}

/**
 * Remove a mutation from the queue (after successful sync)
 */
export async function removeMutation(mutationId: string): Promise<void> {
  try {
    await del(mutationId, mutationStore);
    console.log('[OfflineQueue] Removed mutation from queue:', mutationId);
  } catch (error) {
    console.error('[OfflineQueue] Error removing mutation:', error);
  }
}

/**
 * Increment retry count for a mutation
 */
export async function incrementRetryCount(mutationId: string): Promise<void> {
  try {
    const mutation = await get<QueuedMutation>(mutationId, mutationStore);
    if (mutation) {
      mutation.retryCount = (mutation.retryCount || 0) + 1;
      await set(mutationId, mutation, mutationStore);
    }
  } catch (error) {
    console.error('[OfflineQueue] Error incrementing retry count:', error);
  }
}

/**
 * Clear all synced mutations from the queue
 */
export async function clearSynced(): Promise<void> {
  try {
    const allKeys = await keys(mutationStore);
    let clearedCount = 0;

    for (const key of allKeys) {
      if (typeof key === 'string') {
        const mutation = await get<QueuedMutation>(key, mutationStore);
        if (mutation && mutation.synced) {
          await del(key, mutationStore);
          clearedCount++;
        }
      }
    }

    console.log('[OfflineQueue] Cleared', clearedCount, 'synced mutations');
  } catch (error) {
    console.error('[OfflineQueue] Error clearing synced mutations:', error);
  }
}

/**
 * Get count of pending mutations
 */
export async function getPendingCount(): Promise<number> {
  const pending = await getPendingMutations();
  return pending.length;
}

/**
 * Clear all mutations (for testing/debugging)
 */
export async function clearAll(): Promise<void> {
  try {
    const allKeys = await keys(mutationStore);
    for (const key of allKeys) {
      if (typeof key === 'string') {
        await del(key, mutationStore);
      }
    }
    console.log('[OfflineQueue] Cleared all mutations');
  } catch (error) {
    console.error('[OfflineQueue] Error clearing all mutations:', error);
  }
}

/**
 * Queue a submission for retry (for transient errors)
 * This uses localStorage as a fallback retry mechanism
 */
export async function queueRetryUpload(data: any): Promise<void> {
  try {
    const pending = JSON.parse(localStorage.getItem('pendingUploads') || '[]');
    pending.push({
      ...data,
      queuedAt: Date.now(),
      retryCount: 0,
    });
    localStorage.setItem('pendingUploads', JSON.stringify(pending));
    console.log('[OfflineQueue] Queued submission for retry:', data);
  } catch (error) {
    console.error('[OfflineQueue] Failed to queue retry upload:', error);
  }
}

/**
 * Get pending retry uploads
 */
export function getPendingRetryUploads(): any[] {
  try {
    return JSON.parse(localStorage.getItem('pendingUploads') || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear pending retry uploads
 */
export function clearPendingRetryUploads(): void {
  try {
    localStorage.removeItem('pendingUploads');
  } catch (error) {
    console.error('[OfflineQueue] Failed to clear pending retry uploads:', error);
  }
}

/**
 * Remove a specific retry upload
 */
export function removeRetryUpload(index: number): void {
  try {
    const pending = JSON.parse(localStorage.getItem('pendingUploads') || '[]');
    pending.splice(index, 1);
    localStorage.setItem('pendingUploads', JSON.stringify(pending));
  } catch (error) {
    console.error('[OfflineQueue] Failed to remove retry upload:', error);
  }
}

/**
 * Reconstruct payload from stored format (for syncing)
 */
export function reconstructPayload(mutation: QueuedMutation): any {
  if (mutation.isFormData && mutation.payload && typeof mutation.payload === 'object' && 'data' in mutation.payload && 'files' in mutation.payload) {
    return deserializeFormData(mutation.payload as { data: Record<string, any>, files: Array<{ name: string, data: string, type: string }> });
  }
  return mutation.payload;
}
